import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  TextField,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  useBusinesses,
  useLocations,
  useLocationsForBusiness,
} from "../../../../../stores/MasterTables/masterTables.queries";
import {
  useCreateTss,
  useUpdateTss,
  useChangeAdminPin,
  useAuthenticateAdmin,
  useCreateClient,
} from "../../../../../stores/Taxation/taxation.mutations";

interface TSSCreationModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const steps = [
  "Select Business & Location",
  "Create TSS",
  "Set Admin PIN",
  "Authenticate",
  "Initialize TSS",
  "Create Client",
];

const TSSCreationModal: React.FC<TSSCreationModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [businessId, setBusinessId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");

  // TSS Info from creation
  const [tssId, setTssId] = useState("");

  // Fetch businesses and locations for dropdowns
  const { data: businesses = [], isLoading: businessesLoading } =
    useBusinesses();
  const { data: locations = [], isLoading: locationsLoading } = useLocationsForBusiness(businessId);

  // Mutation hooks
  const createTssMutation = useCreateTss();
  const updateTssMutation = useUpdateTss();
  const changeAdminPinMutation = useChangeAdminPin();
  const authenticateAdminMutation = useAuthenticateAdmin();
  const createClientMutation = useCreateClient();

  const loading =
    createTssMutation.isPending ||
    updateTssMutation.isPending ||
    changeAdminPinMutation.isPending ||
    authenticateAdminMutation.isPending ||
    createClientMutation.isPending;

  const handleNext = async () => {
    setError(null);

    try {
      switch (activeStep) {
        case 0: // Validate selection
          if (!businessId || !locationId) {
            setError("Please select both business and location");
            return;
          }
          setActiveStep(1);
          break;

        case 1: // Create TSS
          const tssInfo = await createTssMutation.mutateAsync({
            businessId,
            locationId,
          });
          setTssId(tssInfo.id);

          // Immediately update to UNINITIALIZED
          await updateTssMutation.mutateAsync({
            tssId: tssInfo.id,
            state: "UNINITIALIZED" as any,
          });
          setActiveStep(2);
          break;

        case 2: // Set Admin PIN
          if (!newPin || newPin !== confirmPin) {
            setError("PIN fields must match and cannot be empty");
            return;
          }
          if (newPin.length < 6) {
            setError("PIN must be at least 6 characters long");
            return;
          }

          await changeAdminPinMutation.mutateAsync({
            tssId,
            dto: { newPin },
          });
          setActiveStep(3);
          break;

        case 3: // Authenticate
          await authenticateAdminMutation.mutateAsync(tssId);
          setActiveStep(4);
          break;

        case 4: // Initialize TSS
          await updateTssMutation.mutateAsync({
            tssId,
            state: "INITIALIZED" as any,
          });
          setActiveStep(5);
          break;

        case 5: // Create Client
          await createClientMutation.mutateAsync(tssId);
          onSuccess();
          handleClose();
          break;
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "An error occurred during TSS creation";
      setError(errorMessage);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleClose = () => {
    setActiveStep(0);
    setBusinessId("");
    setLocationId("");
    setNewPin("");
    setConfirmPin("");
    setTssId("");
    setError(null);
    onClose();
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ minWidth: 400 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              TSS Configuration
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Business</InputLabel>
              <Select
                value={businessId}
                label="Business"
                onChange={(e) => setBusinessId(e.target.value)}
                disabled={businessesLoading}
              >
                {businesses.map((business: any) => (
                  <MenuItem key={business.id} value={business.id}>
                    {business.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Location</InputLabel>
              <Select
                value={locationId}
                label="Location"
                onChange={(e) => setLocationId(e.target.value)}
                disabled={locationsLoading}
              >
                {locations.map((location: any) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mt: 2 }}>
              Select the business and location for which the TSS will be
              created.
            </Alert>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Ready to Create TSS
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click 'Next' to create a new Technical Security System for:
            </Typography>
            <Box
              sx={{
                bgcolor: "grey.100",
                p: 2,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Business:</strong>{" "}
                {businesses.find((b: any) => b.id === businessId)?.name ||
                  "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Location:</strong>{" "}
                {locations.find((l: any) => l.id === locationId)?.name || "N/A"}
              </Typography>
            </Box>
            <Alert severity="info" sx={{ mt: 2 }}>
              The TSS will be created and automatically set to UNINITIALIZED
              state.
            </Alert>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ minWidth: 300 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Configure Admin PIN
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Set a secure PIN for the TSS administrator access.
            </Typography>
            <TextField
              fullWidth
              type="password"
              label="New Admin PIN"
              value={newPin}
              onChange={(e) => setNewPin(e.target.value)}
              sx={{ mb: 3 }}
              inputProps={{ minLength: 4 }}
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm Admin PIN"
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value)}
              error={newPin !== confirmPin && confirmPin.length > 0}
              helperText={
                newPin !== confirmPin && confirmPin.length > 0
                  ? "PINs do not match"
                  : "PIN must be at least 4 characters"
              }
            />
          </Box>
        );

      case 3:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Ready to Authenticate
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click 'Next' to authenticate as admin using the configured PIN.
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Authentication is required before the TSS can be initialized.
            </Alert>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Ready to Initialize TSS
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click 'Next' to update the TSS status to INITIALIZED state.
            </Typography>
            <Alert severity="info" sx={{ mt: 2 }}>
              Once initialized, the TSS will be ready for client registration.
            </Alert>
          </Box>
        );

      case 5:
        return (
          <Box sx={{ py: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Ready to Create Client
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click 'Finish' to create the TSS client and complete the setup.
            </Typography>
            <Alert severity="success" sx={{ mt: 2 }}>
              This is the final step. Your TSS will be ready for transactions!
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  const isStepOptional = (step: number) => {
    return false; // No optional steps in our flow
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return businessId && locationId;
      case 2:
        return (
          newPin && confirmPin && newPin === confirmPin && newPin.length >= 4
        );
      default:
        return true;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New TSS</DialogTitle>

      <DialogContent>
        <Box sx={{ width: "100%", mt: 2 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label, index) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: { optional?: React.ReactNode } = {};

              if (isStepOptional(index)) {
                labelProps.optional = (
                  <Typography variant="caption">Optional</Typography>
                );
              }

              return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>

          {error && (
            <Alert
              severity="error"
              sx={{
                mt: 2,
                mb: 2,
                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
              onClose={() => setError(null)}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Error
              </Typography>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          <Box sx={{ mt: 3, mb: 2 }}>{renderStepContent(activeStep)}</Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={!canProceed() || loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {activeStep === steps.length - 1 ? "Finish" : "Next"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TSSCreationModal;
